import type {
  DocsBridge,
  DocsDocument,
  DocsDocumentNavigationTarget,
  DocsDocumentSummary,
  DocsPublishResult,
  DocsHostContext,
  DocsRevisionListItem,
  DocsSearchItem,
} from '@/bridge/types';
import { BlockNoteSchema, type PartialBlock } from '@blocknote/core';
import { filterSuggestionItems } from '@blocknote/core/extensions';
import { zh } from '@blocknote/core/locales';
import {
  getMultiColumnSlashMenuItems,
  locales as multiColumnLocales,
  withMultiColumn,
} from '@blocknote/xl-multi-column';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';
import {
  FormattingToolbar,
  FormattingToolbarController,
  getDefaultReactSlashMenuItems,
  SuggestionMenuController,
  getFormattingToolbarItems,
  useComponentsContext,
  useCreateBlockNote,
  useSelectedBlocks,
} from '@blocknote/react';
import { MantineProvider } from '@mantine/core';
import {
  Alert,
  App as AntdApp,
  Button,
  ConfigProvider,
  Empty,
  Image as AntdImage,
  Input,
  message,
  Modal,
  Skeleton,
  Tag,
  theme as antdTheme,
  Tooltip,
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import {
  ChevronRight,
  Clock3,
  Copy,
  Edit3,
  FileText,
  FolderOpen,
  History,
  Link2,
  Maximize2,
  PanelRightClose,
  Save,
  Search,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import useSWR from 'swr';
import biLogo from '../../../src/app/assets/images/logo.svg';
import './styles.css';

const BI_FONT_FAMILY =
  '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';
const DOCS_IMAGE_PREVIEW_Z_INDEX = 1200;

interface DocsAppProps {
  bridge: DocsBridge;
  initialContext: DocsHostContext;
}

interface HeadingItem {
  id: string;
  level: number;
  text: string;
}

interface DocumentDirectoryNode {
  key: string;
  label: string;
  children: DocumentDirectoryNode[];
  documents: DocsDocumentSummary[];
}

interface DocumentDirectoryTree {
  children: DocumentDirectoryNode[];
  documents: DocsDocumentSummary[];
}

interface DocumentAnchorInfo {
  id: string;
  link: string;
  text: string;
}

interface InternalDocumentLinkTarget {
  documentId?: string;
  documentKey?: string;
  revisionId?: string;
  targetBlockId?: string;
  title: string;
}

const DocumentAnchorActionContext = createContext<(() => void) | null>(null);
const docsZhDictionary = {
  ...zh,
  multi_column: multiColumnLocales.zh,
  file_panel: {
    ...zh.file_panel,
    upload: {
      ...zh.file_panel.upload,
      upload_error: '上传失败',
    },
  },
};

const docsSchema = withMultiColumn(BlockNoteSchema.create());

function DocumentAnchorToolbarButton() {
  const createAnchor = useContext(DocumentAnchorActionContext);
  const Components = useComponentsContext();
  if (!createAnchor || !Components) {
    return null;
  }
  return (
    <Components.FormattingToolbar.Button
      className="bn-button"
      icon={<Link2 size={16} />}
      label="设置锚点"
      mainTooltip="设置锚点"
      onClick={createAnchor}
    />
  );
}

function DocumentFormattingToolbar() {
  return (
    <FormattingToolbar>
      {getFormattingToolbarItems()}
      <DocumentAnchorToolbarButton />
    </FormattingToolbar>
  );
}

/**
 * BlockNote rejects an empty initialContent array. Empty PosBI documents are
 * valid, so represent them with a single empty paragraph at the editor edge.
 */
function normalizeDocumentBlocks(blocks: unknown): PartialBlock[] {
  if (Array.isArray(blocks) && blocks.length > 0) {
    return blocks as PartialBlock[];
  }
  return [{ type: 'paragraph', content: '' }];
}

function toBlockText(block: PartialBlock | Record<string, unknown>) {
  const content = block.content;
  if (typeof content === 'string') {
    return content;
  }
  if (!Array.isArray(content)) {
    return '';
  }
  return content
    .map(item => {
      if (typeof item === 'string') {
        return item;
      }
      if (item && typeof item === 'object' && 'text' in item) {
        return String(item.text || '');
      }
      return '';
    })
    .join('');
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function renderHighlightedText(text: string, keyword: string) {
  const normalizedKeyword = keyword.trim();
  if (!normalizedKeyword) {
    return text;
  }
  const normalizedKeywordLowerCase = normalizedKeyword.toLowerCase();
  return text
    .split(new RegExp(`(${escapeRegExp(normalizedKeyword)})`, 'gi'))
    .map((part, index) =>
      part.toLowerCase() === normalizedKeywordLowerCase ? (
        <mark className="posbi-docs-search-highlight" key={`${part}_${index}`}>
          {part}
        </mark>
      ) : (
        part
      ),
    );
}

function getHeadings(blocks: unknown[]): HeadingItem[] {
  return blocks.reduce<HeadingItem[]>((items, item) => {
    const block = item as PartialBlock & { id?: string };
    if (block.type !== 'heading' || !block.id) {
      return items;
    }
    const level = Number(
      (block.props as { level?: number } | undefined)?.level,
    );
    items.push({
      id: block.id,
      level: Number.isFinite(level) ? level : 2,
      text: toBlockText(block),
    });
    return items;
  }, []);
}

function formatDate(value?: string) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
}

function findBlockElement(container: HTMLElement, blockId: string) {
  return Array.from(container.querySelectorAll<HTMLElement>('[data-id]')).find(
    element => element.dataset.id === blockId,
  );
}

function resolveInternalDocumentLink(
  href: string,
  title: string,
): InternalDocumentLinkTarget | null {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) {
    return null;
  }

  const documentMatch = url.pathname.match(/^\/documents\/([^/]+)\/?$/);
  const documentKey =
    url.pathname === '/documents'
      ? url.searchParams.get('documentKey')?.trim()
      : undefined;
  if (!documentMatch && !documentKey) {
    return null;
  }

  return {
    documentId: documentMatch
      ? decodeURIComponent(documentMatch[1])
      : undefined,
    documentKey: documentKey || undefined,
    revisionId: url.searchParams.get('revisionId') || undefined,
    targetBlockId: url.hash ? decodeURIComponent(url.hash.slice(1)) : undefined,
    title,
  };
}

function DocumentEditor({
  blocks,
  documentId,
  editable,
  onBlocksChange,
  onOpenDocumentLink,
  onUploadFile,
  targetBlockId,
}: {
  blocks: PartialBlock[];
  documentId: string;
  editable: boolean;
  onBlocksChange: (blocks: unknown[]) => void;
  onOpenDocumentLink: (
    target: InternalDocumentLinkTarget,
  ) => Promise<void> | void;
  onUploadFile: (file: File) => Promise<string>;
  targetBlockId?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const initialContent = useMemo(
    () => normalizeDocumentBlocks(blocks),
    [blocks],
  );
  const editor = useCreateBlockNote({
    dictionary: docsZhDictionary,
    initialContent,
    schema: docsSchema,
    uploadFile: onUploadFile,
  });
  const slashMenuItems = useMemo(
    () => [
      ...getDefaultReactSlashMenuItems(editor),
      ...getMultiColumnSlashMenuItems(editor),
    ],
    [editor],
  );
  const selectedBlocks = useSelectedBlocks(editor);
  const highlightFrameRef = useRef<number | null>(null);
  const [anchorInfo, setAnchorInfo] = useState<DocumentAnchorInfo | null>(null);
  const [anchorModalOpen, setAnchorModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const openDocumentLink = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (
        editable ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey
      ) {
        return;
      }
      const element =
        event.target instanceof Element
          ? event.target.closest<HTMLAnchorElement>('a[href]')
          : null;
      if (!element || !rootRef.current?.contains(element)) {
        return;
      }
      const target = resolveInternalDocumentLink(
        element.getAttribute('href') || '',
        element.textContent?.trim() || '目标文档',
      );
      if (!target) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      void onOpenDocumentLink(target);
    },
    [editable, onOpenDocumentLink],
  );

  const openImagePreview = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (
        editable ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey
      ) {
        return;
      }
      const image =
        event.target instanceof Element
          ? event.target.closest<HTMLImageElement>('img')
          : null;
      if (!image || !rootRef.current?.contains(image)) {
        return;
      }
      const imageUrl = image.currentSrc || image.src;
      if (!imageUrl) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setPreviewImageUrl(imageUrl);
    },
    [editable],
  );

  const handleEditorClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      openImagePreview(event);
      openDocumentLink(event);
    },
    [openDocumentLink, openImagePreview],
  );

  const scrollToBlock = useCallback((blockId: string) => {
    const container = rootRef.current;
    if (!container) {
      return;
    }
    const target = findBlockElement(container, blockId);
    if (!target) {
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (highlightFrameRef.current !== null) {
      window.cancelAnimationFrame(highlightFrameRef.current);
    }
    container
      .querySelectorAll('.posbi-docs-target-block')
      .forEach(element => element.classList.remove('posbi-docs-target-block'));
    highlightFrameRef.current = window.requestAnimationFrame(() => {
      target.classList.add('posbi-docs-target-block');
      highlightFrameRef.current = null;
    });
  }, []);

  useEffect(
    () => () => {
      if (highlightFrameRef.current !== null) {
        window.cancelAnimationFrame(highlightFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!targetBlockId) {
      return;
    }
    const timer = window.setTimeout(() => scrollToBlock(targetBlockId), 80);
    return () => window.clearTimeout(timer);
  }, [scrollToBlock, targetBlockId]);

  const createAnchor = useCallback(() => {
    const currentBlocks = editor.getSelection()?.blocks ||
      selectedBlocks || [editor.getTextCursorPosition().block];
    if (currentBlocks.length !== 1) {
      message.warning('一次只能为一个段落设置锚点');
      return;
    }
    const block = currentBlocks[0];
    if (!block?.id) {
      message.error('当前段落还没有可用的锚点 ID');
      return;
    }
    const id = block.id;
    setAnchorInfo({
      id,
      link: new URL(
        `/documents/${encodeURIComponent(documentId)}#${encodeURIComponent(
          id,
        )}`,
        window.location.origin,
      ).toString(),
      text: toBlockText(block as PartialBlock) || '当前内容块',
    });
    setAnchorModalOpen(true);
  }, [documentId, editor, selectedBlocks]);

  const copyAnchorValue = useCallback(async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label}已复制`);
    } catch {
      message.error(`无法自动复制，请手动复制${label}`);
    }
  }, []);

  return (
    <div
      className={`posbi-docs-editor${
        editable ? '' : ' posbi-docs-editor-readonly'
      }`}
      ref={rootRef}
      onClickCapture={handleEditorClick}
    >
      <DocumentAnchorActionContext.Provider value={createAnchor}>
        <BlockNoteView
          editor={editor}
          editable={editable}
          formattingToolbar={false}
          onChange={() => onBlocksChange(editor.document)}
          slashMenu={false}
          theme="light"
        >
          <SuggestionMenuController
            getItems={async query =>
              filterSuggestionItems(slashMenuItems, query)
            }
            triggerCharacter="/"
          />
          <FormattingToolbarController
            formattingToolbar={DocumentFormattingToolbar}
          />
        </BlockNoteView>
      </DocumentAnchorActionContext.Provider>
      <Modal
        closable={false}
        footer={null}
        open={anchorModalOpen}
        title={
          <div className="posbi-docs-modal-title">
            <span>段落锚点</span>
            <Button
              aria-label="关闭锚点窗口"
              icon={<X size={16} />}
              type="text"
              onClick={() => setAnchorModalOpen(false)}
            />
          </div>
        }
        onCancel={() => setAnchorModalOpen(false)}
      >
        {anchorInfo ? (
          <div className="posbi-docs-anchor-dialog">
            <div className="posbi-docs-anchor-selection">
              <span>锚点内容</span>
              <strong>{anchorInfo.text}</strong>
            </div>
            <label>
              <span>锚点 ID</span>
              <div>
                <Input readOnly value={anchorInfo.id} />
                <Button
                  icon={<Copy size={15} />}
                  onClick={() => void copyAnchorValue(anchorInfo.id, '锚点 ID')}
                >
                  复制
                </Button>
              </div>
            </label>
            <label>
              <span>锚点链接</span>
              <div>
                <Input readOnly value={anchorInfo.link} />
                <Button
                  icon={<Copy size={15} />}
                  onClick={() =>
                    void copyAnchorValue(anchorInfo.link, '锚点链接')
                  }
                >
                  复制
                </Button>
              </div>
            </label>
            <p>新建或修改段落后，请先保存并发布文档，再用于主页跳转。</p>
          </div>
        ) : null}
      </Modal>
      {previewImageUrl ? (
        <AntdImage
          alt="文档图片预览"
          src={previewImageUrl}
          preview={{
            visible: true,
            src: previewImageUrl,
            getContainer: () => document.body,
            zIndex: DOCS_IMAGE_PREVIEW_Z_INDEX,
            onVisibleChange: visible => {
              if (!visible) {
                setPreviewImageUrl(null);
              }
            },
          }}
          style={{ display: 'none' }}
        />
      ) : null}
    </div>
  );
}

function useDocument(
  bridge: DocsBridge,
  context: DocsHostContext,
): {
  document?: DocsDocument;
  error?: Error;
  loading: boolean;
  refresh: () => Promise<DocsDocument | undefined>;
  update: (document: DocsDocument) => Promise<DocsDocument | undefined>;
} {
  const key = context.documentId
    ? ['posbi-doc', context.documentId, context.revisionId || 'latest']
    : null;
  const result = useSWR<DocsDocument>(key, async () => {
    const response = await bridge.request<DocsDocument>({
      method: 'document.get',
      params: {
        documentId: context.documentId,
        revisionId: context.revisionId,
      },
    });
    if (!response.ok) {
      throw new Error(response.error.message);
    }
    return response.data;
  });

  return {
    document: result.data,
    error: result.error,
    loading: result.isLoading,
    refresh: () => result.mutate(),
    update: document => result.mutate(document, { revalidate: false }),
  };
}

function useDocumentDirectory(
  bridge: DocsBridge,
  enabled: boolean,
): {
  documents: DocsDocumentSummary[];
  error?: Error;
  loading: boolean;
  refresh: () => Promise<DocsDocumentSummary[] | undefined>;
} {
  const key = enabled ? ['posbi-doc-directory', 'global'] : null;
  const result = useSWR<DocsDocumentSummary[]>(key, async () => {
    const response = await bridge.request<DocsDocumentSummary[]>({
      method: 'document.list',
    });
    if (!response.ok) {
      throw new Error(response.error.message);
    }
    return response.data;
  });

  return {
    documents: result.data || [],
    error: result.error,
    loading: result.isLoading,
    refresh: () => result.mutate(),
  };
}

function compareDirectoryLabels(left: string, right: string) {
  const directoryOrder = [
    '产品介绍',
    '产品概览',
    '产品操作',
    '功能指南',
    '操作手册',
    '数据建模',
    '数据连接与模型',
    '进阶配置',
    '组织与协同',
    '账号、角色与组织',
    '账号与组织',
    '集成与推送',
    '监控与分析',
    '智能功能',
    '报表与可视化',
    '图表与仪表板',
    '图表与仪表板制作',
    '图表制作',
    '多端展示',
    '数据应用与报表市场',
    '数据采集与应用',
    '分享、归档与恢复',
    '分享与归档',
    '经营分析',
    '监控与预警',
  ];
  const getPriority = (label: string) => {
    const index = directoryOrder.indexOf(label);
    if (index >= 0) return index;
    if (label.includes('总则')) return directoryOrder.length;
    if (label.includes('介绍')) return directoryOrder.length + 1;
    return directoryOrder.length + 2;
  };
  return (
    getPriority(left) - getPriority(right) || left.localeCompare(right, 'zh-CN')
  );
}

function compareDirectoryDocuments(
  left: DocsDocumentSummary,
  right: DocsDocumentSummary,
) {
  const sortOrderDifference = (right.sortOrder || 0) - (left.sortOrder || 0);
  if (sortOrderDifference !== 0) {
    return sortOrderDifference;
  }
  const getPriority = (document: DocsDocumentSummary) => {
    const title = document.title.trim();
    if (document.slug === 'posbi-package-pospal-bi-general') return 0;
    if (title.includes('产品介绍')) return 1;
    if (title.includes('介绍')) return 2;
    if (title.includes('产品总则')) return 3;
    if (title.includes('总则')) return 4;
    return 5;
  };
  return (
    getPriority(left) - getPriority(right) ||
    left.title.localeCompare(right.title, 'zh-CN')
  );
}

function buildDocumentDirectory(
  documents: DocsDocumentSummary[],
): DocumentDirectoryTree {
  interface MutableDirectoryNode {
    key: string;
    label: string;
    children: Map<string, MutableDirectoryNode>;
    documents: DocsDocumentSummary[];
  }

  const root: MutableDirectoryNode = {
    key: '__root__',
    label: '',
    children: new Map(),
    documents: [],
  };

  documents.forEach(document => {
    const breadcrumb = (document.breadcrumb || [])
      .map(item => item.trim())
      .filter(Boolean);
    const path = breadcrumb;
    let branch = root;
    path.forEach((label, index) => {
      let child = branch.children.get(label);
      if (!child) {
        child = {
          key: [...path.slice(0, index), label]
            .map(encodeURIComponent)
            .join('/'),
          label,
          children: new Map(),
          documents: [],
        };
        branch.children.set(label, child);
      }
      branch = child;
    });
    branch.documents.push(document);
  });

  const toDirectoryNode = (
    node: MutableDirectoryNode,
  ): DocumentDirectoryNode => ({
    key: node.key,
    label: node.label,
    children: Array.from(node.children.values())
      .sort((left, right) => compareDirectoryLabels(left.label, right.label))
      .map(toDirectoryNode),
    documents: [...node.documents].sort(compareDirectoryDocuments),
  });

  return {
    children: Array.from(root.children.values())
      .sort((left, right) => compareDirectoryLabels(left.label, right.label))
      .map(toDirectoryNode),
    documents: [...root.documents].sort(compareDirectoryDocuments),
  };
}

interface DocumentDirectoryNodesProps {
  activeDocumentId: string;
  depth?: number;
  nodes: DocumentDirectoryNode[];
  onOpenDocument: (documentId: string) => void;
}

function directoryContainsDocument(
  node: DocumentDirectoryNode,
  documentId: string,
): boolean {
  return (
    node.documents.some(document => document.id === documentId) ||
    node.children.some(child => directoryContainsDocument(child, documentId))
  );
}

function DocumentDirectoryNodes({
  activeDocumentId,
  depth = 0,
  nodes,
  onOpenDocument,
}: DocumentDirectoryNodesProps) {
  return (
    <>
      {nodes.map(node => (
        <details
          className="posbi-docs-directory-node"
          key={node.key}
          open={directoryContainsDocument(node, activeDocumentId)}
        >
          <summary style={{ paddingLeft: 8 + depth * 14 }}>
            <ChevronRight className="posbi-docs-directory-chevron" size={14} />
            <FolderOpen size={15} />
            <span>{node.label}</span>
          </summary>
          <div>
            {node.documents.map(item => (
              <button
                type="button"
                key={item.id}
                className={`posbi-docs-directory-item ${
                  item.id === activeDocumentId ? 'is-active' : ''
                }`}
                style={{ paddingLeft: 30 + depth * 14 }}
                title={item.title}
                onClick={() => onOpenDocument(item.id)}
              >
                <FileText size={14} />
                <span>{item.title}</span>
              </button>
            ))}
            <DocumentDirectoryNodes
              activeDocumentId={activeDocumentId}
              depth={depth + 1}
              nodes={node.children}
              onOpenDocument={onOpenDocument}
            />
          </div>
        </details>
      ))}
    </>
  );
}

export function DocsApp({ bridge, initialContext }: DocsAppProps) {
  const [context, setContext] = useState(initialContext);
  const [editing, setEditing] = useState(false);
  const [draftBlocks, setDraftBlocks] = useState<unknown[]>([]);
  const [revisions, setRevisions] = useState<DocsRevisionListItem[]>([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [deletingRevisionId, setDeletingRevisionId] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const editReturnRevisionIdRef = useRef<string>();
  const directorySidebarRef = useRef<HTMLElement>(null);
  const [query, setQuery] = useState('');
  const [currentDocumentResults, setCurrentDocumentResults] = useState<
    DocsSearchItem[]
  >([]);
  const [otherDocumentResults, setOtherDocumentResults] = useState<
    DocsSearchItem[]
  >([]);
  const [searching, setSearching] = useState(false);
  const {
    document,
    error,
    loading,
    refresh,
    update: updateDocument,
  } = useDocument(bridge, context);
  const variant = context.variant || 'dock';
  const {
    documents: directoryDocuments,
    error: directoryError,
    loading: directoryLoading,
    refresh: refreshDirectory,
  } = useDocumentDirectory(bridge, variant === 'page');
  const documentDirectory = useMemo(
    () => buildDocumentDirectory(directoryDocuments),
    [directoryDocuments],
  );

  useLayoutEffect(() => {
    if (directoryLoading) {
      return;
    }
    const sidebar = directorySidebarRef.current;
    const activeItem = sidebar?.querySelector<HTMLElement>(
      '.posbi-docs-directory-item.is-active',
    );
    if (!sidebar || !activeItem || sidebar.clientHeight === 0) {
      return;
    }
    const sidebarRect = sidebar.getBoundingClientRect();
    const activeItemRect = activeItem.getBoundingClientRect();
    sidebar.scrollTop =
      sidebar.scrollTop +
      activeItemRect.top -
      sidebarRect.top -
      (sidebar.clientHeight - activeItemRect.height) / 2;
  }, [context.documentId, directoryLoading, documentDirectory]);

  useEffect(() => {
    let canceled = false;
    void bridge
      .request<DocsHostContext>({ method: 'context.get' })
      .then(response => {
        if (!canceled && response.ok) {
          setContext(current => ({ ...current, ...response.data }));
        }
      });
    return () => {
      canceled = true;
    };
  }, [bridge]);

  useEffect(() => {
    if (document) {
      setDraftBlocks(normalizeDocumentBlocks(document.revision.blocks));
    }
  }, [document]);

  const loadRevisions = useCallback(async () => {
    if (!document?.canEdit) {
      setRevisions([]);
      return [];
    }
    setRevisionsLoading(true);
    const response = await bridge.request<DocsRevisionListItem[]>({
      method: 'document.revisions.list',
      params: { documentId: context.documentId },
    });
    setRevisionsLoading(false);
    if (!response.ok) {
      throw new Error(response.error.message);
    }
    setRevisions(response.data);
    return response.data;
  }, [bridge, context.documentId, document?.canEdit]);

  useEffect(() => {
    if (!document?.canEdit) {
      setRevisions([]);
      return;
    }
    void loadRevisions().catch(() => undefined);
  }, [document?.canEdit, document?.id, loadRevisions]);

  const headings = useMemo(
    () =>
      getHeadings(
        draftBlocks.length ? draftBlocks : document?.revision.blocks || [],
      ),
    [document?.revision.blocks, draftBlocks],
  );

  const localResults = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return [];
    }
    return headings
      .filter(item => item.text.toLowerCase().includes(normalized))
      .map<DocsSearchItem>(item => ({
        documentId: document?.id || context.documentId,
        revisionId: document?.revision.id || '',
        targetBlockId: item.id,
        title: document?.title || '',
        excerpt: item.text,
      }));
  }, [context.documentId, document, headings, query]);
  const currentResults = currentDocumentResults.length
    ? currentDocumentResults
    : localResults;
  const hasSearchResults =
    currentResults.length > 0 || otherDocumentResults.length > 0;

  useEffect(() => {
    const normalized = query.trim();
    if (normalized.length < 2) {
      setCurrentDocumentResults([]);
      setOtherDocumentResults([]);
      return;
    }
    let canceled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      const [currentResponse, globalResponse] = await Promise.all([
        bridge.request<DocsSearchItem[]>({
          method: 'document.search',
          params: {
            query: normalized,
            documentId: context.documentId,
            limit: 4,
          },
        }),
        bridge.request<DocsSearchItem[]>({
          method: 'document.search',
          params: { query: normalized, limit: 3 },
        }),
      ]);
      if (!canceled) {
        setCurrentDocumentResults(
          currentResponse.ok ? currentResponse.data : [],
        );
        setOtherDocumentResults(
          globalResponse.ok
            ? globalResponse.data.filter(
                result => result.documentId !== context.documentId,
              )
            : [],
        );
        setSearching(false);
      }
    }, 250);
    return () => {
      canceled = true;
      window.clearTimeout(timer);
    };
  }, [bridge, context.documentId, query]);

  const uploadFile = useCallback(
    async (file: File) => {
      const response = await bridge.request<{ url: string }>({
        method: 'document.asset.upload',
        params: { documentId: context.documentId, file },
      });
      if (!response.ok) {
        throw new Error(response.error.message);
      }
      return response.data.url;
    },
    [bridge, context.documentId],
  );

  const openLinkedDocument = useCallback(
    async (target: InternalDocumentLinkTarget) => {
      let documentId = target.documentId;
      if (!documentId && target.documentKey) {
        const response = await bridge.request<DocsDocumentSummary[]>({
          method: 'document.list',
        });
        if (!response.ok) {
          message.error(response.error.message);
          return;
        }
        documentId = response.data.find(
          item => item.slug === target.documentKey,
        )?.id;
      }
      if (!documentId) {
        message.warning(`未找到《${target.title}》`);
        return;
      }
      const navigationTarget: DocsDocumentNavigationTarget = {
        documentId,
        revisionId: target.revisionId,
        targetBlockId: target.targetBlockId,
      };
      bridge.emit('docs.openLinkedDocument', navigationTarget);
    },
    [bridge],
  );

  const saveDraft = async () => {
    if (!document) {
      return;
    }
    setSaving(true);
    const response = await bridge.request<DocsDocument>({
      method: 'document.saveDraft',
      params: {
        documentId: document.id,
        baseRevisionId: document.revision.id,
        latestRevisionId: revisions[0]?.id || document.revision.id,
        blocks: normalizeDocumentBlocks(draftBlocks),
      },
    });
    setSaving(false);
    if (!response.ok) {
      message.error(response.error.message);
      return;
    }
    setContext(current => ({
      ...current,
      revisionId: response.data.revision.id,
      targetBlockId: undefined,
    }));
    bridge.emit('docs.openDocument', {
      documentId: response.data.id,
      revisionId: response.data.revision.id,
    });
    editReturnRevisionIdRef.current = undefined;
    setEditing(false);
    await loadRevisions().catch(() => undefined);
    message.success('草稿已保存');
  };

  const publish = async () => {
    if (
      !document?.canEdit ||
      editing ||
      publishing ||
      unpublishing ||
      document.revision.id === document.publishedRevisionId
    ) {
      return;
    }
    setPublishing(true);
    try {
      const response = await bridge.request<DocsPublishResult>({
        method: 'document.publish',
        params: {
          documentId: document.id,
          revisionId: document.revision.id,
        },
      });
      if (!response.ok) {
        throw new Error(response.error.message);
      }
      const result = response.data;
      await updateDocument({
        ...document,
        status: 'published',
        publishedRevisionId: result.publishedRevisionId,
        updatedAt: result.publishedAt,
        revision: {
          ...document.revision,
          publishedAt: result.publishedAt,
        },
      });
      setRevisions(current =>
        current.map(revision =>
          revision.id === result.publishedRevisionId
            ? { ...revision, publishedAt: result.publishedAt }
            : revision,
        ),
      );
      message.success('文档已发布');
    } catch (publishError) {
      message.error(
        publishError instanceof Error ? publishError.message : '文档发布失败',
      );
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async () => {
    if (
      !document?.canEdit ||
      editing ||
      publishing ||
      unpublishing ||
      document.revision.id !== document.publishedRevisionId
    ) {
      return;
    }
    setUnpublishing(true);
    try {
      const response = await bridge.request<DocsDocument>({
        method: 'document.unpublish',
        params: {
          documentId: document.id,
          revisionId: document.publishedRevisionId,
        },
      });
      if (!response.ok) {
        throw new Error(response.error.message);
      }
      await updateDocument(response.data);
      await refreshDirectory().catch(() => undefined);
      message.success('文档已下架，转为未发布');
    } catch (unpublishError) {
      message.error(
        unpublishError instanceof Error
          ? unpublishError.message
          : '文档下架失败',
      );
    } finally {
      setUnpublishing(false);
    }
  };

  const startEditing = async () => {
    if (!document || revisionsLoading) {
      return;
    }
    let availableRevisions = revisions;
    if (!availableRevisions.length) {
      try {
        availableRevisions = await loadRevisions();
      } catch (loadError) {
        message.error(
          loadError instanceof Error ? loadError.message : '版本加载失败',
        );
        return;
      }
    }
    editReturnRevisionIdRef.current = context.revisionId;
    const latestRevision = availableRevisions[0];
    if (latestRevision && latestRevision.id !== document.revision.id) {
      setContext(current => ({
        ...current,
        revisionId: latestRevision.id,
        targetBlockId: undefined,
      }));
    }
    setEditing(true);
  };

  const exitEditing = useCallback(() => {
    if (!editing) {
      return;
    }
    Modal.confirm({
      title: '退出编辑？',
      content: '当前正在编辑，未保存的修改将丢失。',
      okText: '退出编辑',
      cancelText: '继续编辑',
      onOk: () => {
        setEditing(false);
        setContext(current => ({
          ...current,
          revisionId: editReturnRevisionIdRef.current,
          targetBlockId: undefined,
        }));
        editReturnRevisionIdRef.current = undefined;
        if (document) {
          setDraftBlocks(normalizeDocumentBlocks(document.revision.blocks));
        }
      },
    });
  }, [document, editing]);

  const selectRevision = useCallback(
    (revisionId: string) => {
      if (!document || revisionId === document.revision.id) {
        setVersionModalOpen(false);
        return;
      }
      const navigate = () => {
        setEditing(false);
        editReturnRevisionIdRef.current = undefined;
        setVersionModalOpen(false);
        setContext(current => ({
          ...current,
          revisionId,
          targetBlockId: undefined,
        }));
        bridge.emit('docs.openDocument', {
          documentId: context.documentId,
          revisionId,
        });
      };
      if (!editing) {
        navigate();
        return;
      }
      Modal.confirm({
        title: '切换版本？',
        content: '当前正在编辑，未保存的修改将丢失。',
        okText: '继续切换',
        cancelText: '留在当前版本',
        onOk: navigate,
      });
    },
    [bridge, context.documentId, document, editing],
  );

  const confirmDeleteRevision = useCallback(
    (revision: DocsRevisionListItem) => {
      if (!document || revision.id === document.publishedRevisionId) {
        return;
      }
      Modal.confirm({
        title: `确认删除 v${revision.revisionNo}？`,
        content: '删除后将从数据库中移除该版本，且无法恢复。',
        okText: '删除版本',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          setDeletingRevisionId(revision.id);
          const response = await bridge.request<boolean>({
            method: 'document.revision.delete',
            params: {
              documentId: context.documentId,
              revisionId: revision.id,
            },
          });
          setDeletingRevisionId(undefined);
          if (!response.ok) {
            message.error(response.error.message);
            return;
          }
          const remaining = revisions.filter(item => item.id !== revision.id);
          const isCurrent = document.revision.id === revision.id;
          message.success(`v${revision.revisionNo} 已删除`);
          if (isCurrent) {
            const fallback =
              remaining.find(
                item => item.id === document.publishedRevisionId,
              ) || remaining[0];
            if (fallback) {
              selectRevision(fallback.id);
              return;
            }
          }
          setRevisions(remaining);
          await refresh();
        },
      });
    },
    [bridge, context.documentId, document, refresh, revisions, selectRevision],
  );

  const openVersionManager = useCallback(async () => {
    setVersionModalOpen(true);
    try {
      await loadRevisions();
    } catch (loadError) {
      message.error(
        loadError instanceof Error ? loadError.message : '版本加载失败',
      );
    }
  }, [loadRevisions]);

  const openDirectoryDocument = useCallback(
    (documentId: string) => {
      if (documentId === context.documentId) {
        return;
      }
      const navigate = () => {
        bridge.emit('docs.openDocument', { documentId });
      };
      if (!editing) {
        navigate();
        return;
      }
      Modal.confirm({
        title: '切换文档？',
        content: '当前正在编辑，未保存的修改将丢失。',
        okText: '继续切换',
        cancelText: '留在当前文档',
        onOk: navigate,
      });
    },
    [bridge, context.documentId, editing],
  );

  const openFullPage = () => {
    bridge.emit('docs.openFull', {
      documentId: context.documentId,
      revisionId: context.revisionId,
      targetBlockId: context.targetBlockId,
    });
  };

  const scrollToResult = (result: DocsSearchItem) => {
    if (result.documentId !== context.documentId) {
      bridge.emit('docs.openLinkedDocument', {
        documentId: result.documentId,
        revisionId: result.revisionId,
        targetBlockId: result.targetBlockId,
        title: result.title,
      });
      setQuery('');
      setCurrentDocumentResults([]);
      setOtherDocumentResults([]);
      return;
    }
    bridge.emit('docs.focusBlock', {
      documentId: result.documentId,
      targetBlockId: result.targetBlockId,
    });
    setContext(current => ({
      ...current,
      targetBlockId: result.targetBlockId,
    }));
    setQuery('');
    setCurrentDocumentResults([]);
    setOtherDocumentResults([]);
  };

  const renderSearch = (compact = false) => (
    <div className={`posbi-docs-search ${compact ? 'is-compact' : ''}`}>
      <Input
        allowClear
        prefix={<Search size={15} />}
        placeholder="搜索文档和段落"
        value={query}
        onChange={event => setQuery(event.target.value)}
      />
      {query.trim() ? (
        <div className="posbi-docs-search-results">
          {searching ? <Skeleton active paragraph={{ rows: 2 }} /> : null}
          {!searching && !hasSearchResults ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="没有匹配内容"
            />
          ) : null}
          {!searching && currentResults.length ? (
            <div className="posbi-docs-search-group">
              <div className="posbi-docs-search-group-title">当前文档</div>
              {currentResults.map(result => (
                <button
                  type="button"
                  key={`${result.documentId}_${result.targetBlockId}`}
                  onClick={() => scrollToResult(result)}
                >
                  <FileText size={15} />
                  <span>
                    <strong>
                      {renderHighlightedText(result.title, query)}
                    </strong>
                    <small>
                      {renderHighlightedText(result.excerpt, query)}
                    </small>
                  </span>
                  <ChevronRight size={14} />
                </button>
              ))}
            </div>
          ) : null}
          {!searching &&
          currentResults.length &&
          otherDocumentResults.length ? (
            <div className="posbi-docs-search-divider" />
          ) : null}
          {!searching && otherDocumentResults.length ? (
            <div className="posbi-docs-search-group">
              <div className="posbi-docs-search-group-title">其他文档</div>
              {otherDocumentResults.map(result => (
                <button
                  type="button"
                  key={`${result.documentId}_${result.targetBlockId}`}
                  onClick={() => scrollToResult(result)}
                >
                  <FileText size={15} />
                  <span>
                    <strong>
                      {renderHighlightedText(result.title, query)}
                    </strong>
                    <small>
                      {renderHighlightedText(result.excerpt, query)}
                    </small>
                  </span>
                  <ChevronRight size={14} />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const renderDocument = () => {
    if (loading) {
      return (
        <Skeleton active paragraph={{ rows: variant === 'dock' ? 9 : 14 }} />
      );
    }
    if (error) {
      return (
        <Alert
          showIcon
          type="error"
          message="文档加载失败"
          description={error.message}
          action={<Button onClick={() => void refresh()}>重试</Button>}
        />
      );
    }
    if (!document) {
      return <Empty description="文档不存在或无权查看" />;
    }
    const isPublishedRevision =
      document.revision.id === document.publishedRevisionId;
    const canPublishDraft =
      document.canEdit && !editing && !isPublishedRevision;
    const canUnpublish = document.canEdit && !editing && isPublishedRevision;
    const publicationBusy = publishing || unpublishing;
    const statusAction = canPublishDraft
      ? publish
      : canUnpublish
      ? unpublish
      : undefined;
    let statusLabel = isPublishedRevision ? '已发布' : '草稿（未发布）';
    if (unpublishing) {
      statusLabel = '下架中…';
    } else if (publishing) {
      statusLabel = '发布中…';
    } else if (canUnpublish) {
      statusLabel = '已发布（点击下架）';
    } else if (canPublishDraft) {
      statusLabel = '草稿（点击发布）';
    }
    const documentPath = [
      ...(document.breadcrumb?.length ? document.breadcrumb : ['文档中心']),
      document.title,
    ];
    return (
      <article className="posbi-docs-article">
        <nav className="posbi-docs-breadcrumb" aria-label="当前文档路径">
          <FolderOpen
            aria-hidden="true"
            className="posbi-docs-breadcrumb-icon"
            size={14}
          />
          <ol>
            {documentPath.map((item, index) => (
              <li
                className={
                  index === documentPath.length - 1 ? 'is-current' : undefined
                }
                key={`${index}_${item}`}
              >
                {index > 0 ? <ChevronRight aria-hidden="true" size={13} /> : null}
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </nav>
        <h1>{document.title}</h1>
        <div className="posbi-docs-meta">
          <span>更新于 {formatDate(document.updatedAt)}</span>
          <span>阅读约 {document.estimatedReadingMinutes || 1} 分钟</span>
          {document.canEdit ? (
            <Tag
              aria-label={
                canPublishDraft
                  ? `发布草稿 v${document.revision.revisionNo}`
                  : canUnpublish
                  ? '下架文档，转为未发布'
                  : undefined
              }
              aria-disabled={statusAction ? publicationBusy : undefined}
              className={`posbi-docs-status-tag ${
                statusAction && !publicationBusy ? 'is-actionable' : ''
              }`}
              color={isPublishedRevision ? 'blue' : 'orange'}
              role={statusAction ? 'button' : undefined}
              tabIndex={statusAction && !publicationBusy ? 0 : undefined}
              onClick={publicationBusy ? undefined : statusAction}
              onKeyDown={
                statusAction && !publicationBusy
                  ? event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        statusAction();
                      }
                    }
                  : undefined
              }
            >
              {statusLabel}
            </Tag>
          ) : null}
        </div>
        <DocumentEditor
          key={`${document.id}_${document.revision.id}_${
            editing ? 'edit' : 'view'
          }`}
          blocks={document.revision.blocks as PartialBlock[]}
          documentId={document.id}
          editable={editing}
          onBlocksChange={setDraftBlocks}
          onOpenDocumentLink={openLinkedDocument}
          onUploadFile={uploadFile}
          targetBlockId={context.targetBlockId}
        />
      </article>
    );
  };

  const content =
    variant === 'dock' ? (
      <div className="posbi-docs-dock">
        <header className="posbi-docs-dock-header" data-drag-handle>
          <strong className="posbi-docs-dock-title">帮助文档</strong>
          <div className="posbi-docs-header-actions">
            <Button
              aria-label="全屏查看"
              icon={<Maximize2 size={16} />}
              type="text"
              onClick={openFullPage}
            />
            <Button
              aria-label="关闭文档浮窗"
              icon={<X size={17} />}
              type="text"
              onClick={() => bridge.emit('docs.close')}
            />
          </div>
        </header>
        {renderSearch(true)}
        <main className="posbi-docs-dock-body">{renderDocument()}</main>
      </div>
    ) : (
      <div className="posbi-docs-page">
        <header className="posbi-docs-page-header">
          <button
            type="button"
            className="posbi-docs-page-brand"
            aria-label="返回首页"
            onClick={() => bridge.emit('docs.home')}
          >
            <img className="posbi-docs-brand-logo" src={biLogo} alt="PosBI" />
            <strong>PosBI 文档中心</strong>
          </button>
          {renderSearch()}
          <div className="posbi-docs-page-actions">
            {document?.canEdit ? (
              <>
                {editing ? (
                  <>
                    <Button
                      type="primary"
                      icon={<Save size={16} />}
                      loading={saving}
                      onClick={() => void saveDraft()}
                    >
                      保存草稿
                    </Button>
                    <Button icon={<X size={16} />} onClick={exitEditing}>
                      退出编辑
                    </Button>
                  </>
                ) : (
                  <Button
                    icon={<Edit3 size={16} />}
                    disabled={publishing || unpublishing}
                    loading={revisionsLoading}
                    onClick={() => void startEditing()}
                  >
                    编辑
                  </Button>
                )}
              </>
            ) : null}
          </div>
        </header>
        {editing ? (
          <div className="posbi-docs-editing-banner">
            编辑模式：正文支持块级编辑、拖拽、代码块和图片/视频上传；选中文字后可在格式工具栏设置锚点
          </div>
        ) : null}
        <div className="posbi-docs-page-layout">
          <aside className="posbi-docs-sidebar" ref={directorySidebarRef}>
            <div className="posbi-docs-sidebar-title">
              <span>文档目录</span>
            </div>
            {directoryLoading ? (
              <Skeleton active title={false} paragraph={{ rows: 5 }} />
            ) : directoryError ? (
              <div className="posbi-docs-directory-state">
                <span>目录加载失败</span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => void refreshDirectory()}
                >
                  重试
                </Button>
              </div>
            ) : documentDirectory.children.length ||
              documentDirectory.documents.length ? (
              <div className="posbi-docs-directory">
                {documentDirectory.documents.map(item => (
                  <button
                    type="button"
                    key={item.id}
                    className={`posbi-docs-directory-item ${
                      item.id === context.documentId ? 'is-active' : ''
                    }`}
                    title={item.title}
                    onClick={() => openDirectoryDocument(item.id)}
                  >
                    <FileText size={14} />
                    <span>{item.title}</span>
                  </button>
                ))}
                <DocumentDirectoryNodes
                  key={context.documentId}
                  activeDocumentId={context.documentId}
                  nodes={documentDirectory.children}
                  onOpenDocument={openDirectoryDocument}
                />
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无可见文档"
              />
            )}
          </aside>
          <main className="posbi-docs-page-body">{renderDocument()}</main>
          <aside className="posbi-docs-outline">
            <div className="posbi-docs-outline-content">
              <div className="posbi-docs-outline-title">
                <PanelRightClose size={15} />
                本文内容
              </div>
              {headings.map(item => (
                <button
                  type="button"
                  key={item.id}
                  className={
                    item.id === context.targetBlockId ? 'is-active' : ''
                  }
                  style={{ paddingLeft: 10 + Math.max(0, item.level - 2) * 12 }}
                  onClick={() =>
                    setContext(current => ({
                      ...current,
                      targetBlockId: item.id,
                    }))
                  }
                >
                  {item.text}
                </button>
              ))}
            </div>
            <div className="posbi-docs-outline-meta">
              {document?.canEdit ? (
                <Button
                  className="posbi-docs-version-trigger"
                  icon={<History size={14} />}
                  type="text"
                  onClick={() => void openVersionManager()}
                >
                  <span>当前版本</span>
                  <strong>v{document?.revision.revisionNo || '-'}</strong>
                </Button>
              ) : (
                <div className="posbi-docs-version-trigger is-readonly">
                  <span>当前版本</span>
                  <strong>v{document?.revision.revisionNo || '-'}</strong>
                </div>
              )}
            </div>
          </aside>
        </div>
        <Modal
          centered
          className="posbi-docs-version-modal"
          footer={null}
          open={versionModalOpen}
          title={
            <span className="posbi-docs-version-modal-title">
              <History size={18} />
              <span>文档版本</span>
            </span>
          }
          width={600}
          onCancel={() => setVersionModalOpen(false)}
        >
          {revisionsLoading ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : revisions.length ? (
            <div className="posbi-docs-version-manager">
              <div className="posbi-docs-version-summary">
                <div>
                  <strong>共 {revisions.length} 个版本</strong>
                  <span>按创建时间倒序排列</span>
                </div>
              </div>
              <div className="posbi-docs-version-list">
                {revisions.map(revision => {
                  const isPublished =
                    revision.id === document?.publishedRevisionId;
                  const isCurrent = revision.id === document?.revision.id;
                  const canDelete = !isPublished && revisions.length > 1;
                  return (
                    <div
                      className={`posbi-docs-version-item ${
                        isCurrent ? 'is-current' : ''
                      }`}
                      key={revision.id}
                    >
                      <button
                        aria-current={isCurrent ? 'true' : undefined}
                        className="posbi-docs-version-select"
                        type="button"
                        onClick={() => selectRevision(revision.id)}
                      >
                        <span className="posbi-docs-version-heading">
                          <strong>v{revision.revisionNo}</strong>
                          {isPublished ? (
                            <span className="posbi-docs-version-status is-published">
                              已发布
                            </span>
                          ) : null}
                          {isCurrent ? (
                            <span className="posbi-docs-version-status is-current">
                              正在查看
                            </span>
                          ) : null}
                        </span>
                        <span className="posbi-docs-version-details">
                          <span>
                            <Clock3 size={13} />
                            {formatDate(revision.createdAt)}
                          </span>
                          <span>
                            <UserRound size={13} />
                            {revision.createdBy || '未知用户'}
                          </span>
                        </span>
                      </button>
                      <div className="posbi-docs-version-actions">
                        {!isCurrent ? (
                          <Tooltip
                            classNames={{
                              root: 'posbi-docs-version-tooltip',
                            }}
                            title={
                              isPublished
                                ? '已发布版本受保护，不可删除'
                                : '查看该版本内容'
                            }
                          >
                            <button
                              aria-label={`查看 v${revision.revisionNo}`}
                              className="posbi-docs-version-view-action"
                              type="button"
                              onClick={() => selectRevision(revision.id)}
                            >
                              查看
                              <ChevronRight size={14} />
                            </button>
                          </Tooltip>
                        ) : null}
                        {canDelete ? (
                          <Tooltip
                            classNames={{
                              root: 'posbi-docs-version-tooltip',
                            }}
                            title={`删除 v${revision.revisionNo}`}
                          >
                            <Button
                              aria-label={`删除 v${revision.revisionNo}`}
                              danger
                              icon={<Trash2 size={15} />}
                              loading={deletingRevisionId === revision.id}
                              type="text"
                              onClick={event => {
                                event.stopPropagation();
                                confirmDeleteRevision(revision);
                              }}
                            />
                          </Tooltip>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无历史版本"
            />
          )}
        </Modal>
      </div>
    );

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#1b9aee',
          borderRadius: 4,
          fontFamily: BI_FONT_FAMILY,
          fontSize: 14,
        },
      }}
    >
      <AntdApp className="posbi-docs-app">
        <MantineProvider forceColorScheme="light">
          <div className="posbi-docs-root" data-variant={variant}>
            {content}
          </div>
        </MantineProvider>
      </AntdApp>
    </ConfigProvider>
  );
}
